import { Link } from "react-router-dom";
import {
  ShieldCheck, Fingerprint, Camera, MessageSquareLock, Smartphone,
  Radar, Wallet, BadgeCheck, AlertTriangle, ArrowRight, Lock, Eye,
} from "lucide-react";

const PROTECTIONS = [
  {
    icon: Fingerprint,
    title: "Empreinte digitale (WebAuthn)",
    text: "Vos paiements sont valides par votre empreinte ou Face ID, en plus du code SMS. Votre empreinte ne quitte jamais votre telephone : standard mondial FIDO2, le meme que les banques.",
    color: "from-emerald-500 to-teal-400",
  },
  {
    icon: Camera,
    title: "Selfie anti-fraude",
    text: "Une photo de controle horodatee est capturee avant chaque paiement sensible. En cas de litige, nous savons exactement qui a agi sur le compte.",
    color: "from-orange-500 to-amber-400",
  },
  {
    icon: Smartphone,
    title: "Code SMS (OTP)",
    text: "Chaque inscription et action sensible est confirmee par un code unique envoye par SMS sur votre numero. Personne ne peut utiliser votre compte sans votre telephone.",
    color: "from-sky-500 to-blue-400",
  },
  {
    icon: Radar,
    title: "Detection de fraude proactive",
    text: "Nos systemes surveillent en continu les signaux suspects : connexion depuis une adresse inhabituelle, paiement sans verification photo… et alertent immediatement notre equipe.",
    color: "from-red-500 to-rose-400",
  },
  {
    icon: Lock,
    title: "Pare-feu & anti-intrusion",
    text: "Limitation des tentatives de connexion, verrouillage automatique apres echecs repetes, journal de securite complet : les attaques par force brute sont bloquees.",
    color: "from-violet-500 to-purple-400",
  },
  {
    icon: Wallet,
    title: "Paiements mobile money de confiance",
    text: "Wave, Orange Money, MTN MoMo, Moov Money et CinetPay : vous payez avec les solutions que vous connaissez deja, sans jamais partager vos codes avec le vendeur.",
    color: "from-amber-500 to-yellow-400",
  },
  {
    icon: BadgeCheck,
    title: "Vendeurs verifies & badge Pro",
    text: "Les vendeurs Pro affichent un badge dore apres verification. Numero de telephone confirme par SMS pour tous les deposeurs d'annonces.",
    color: "from-yellow-500 to-amber-400",
  },
  {
    icon: MessageSquareLock,
    title: "Messagerie integree",
    text: "Discutez avec les vendeurs sans reveler votre numero tant que vous ne le souhaitez pas. Tous les echanges restent traces sur la plateforme.",
    color: "from-teal-500 to-cyan-400",
  },
];

const CONSEILS = [
  "Ne payez jamais d'avance sans avoir vu le produit ou verifie le vendeur (badge Pro, anciennete, avis).",
  "Privilegiez les rencontres dans des lieux publics et frequentes pour les remises en main propre.",
  "Ne partagez JAMAIS vos codes Wave / Orange Money / Moov par telephone ou par message — aucun agent Fodindin ne vous les demandera.",
  "Mefiez-vous des prix anormalement bas : c'est le signe n°1 d'une arnaque.",
  "Activez l'empreinte digitale dans vos parametres pour proteger votre compte meme en cas de vol de telephone.",
  "Signalez toute annonce ou comportement suspect a support@fodindin.net : notre equipe agit sous 24h.",
];

export default function ConfianceSecuritePage() {
  return (
    <main className="max-w-7xl mx-auto px-6 py-12" data-testid="confiance-securite-page">
      {/* Hero */}
      <div className="text-center max-w-3xl mx-auto">
        <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-emerald-600 bg-emerald-50 px-4 py-1.5 rounded-full">
          <ShieldCheck size={14} /> Confiance & Securite
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight mt-5">
          Acheter et vendre en toute <span className="text-orange-500">confiance</span>
        </h1>
        <p className="text-[var(--ink-soft)] mt-4 text-base md:text-lg">
          Fodindin protege chaque etape de vos transactions avec des technologies de niveau bancaire,
          adaptees aux realites de l&apos;Afrique de l&apos;Ouest.
        </p>
      </div>

      {/* Protections grid */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12" data-testid="protections-grid">
        {PROTECTIONS.map((p) => (
          <article key={p.title} className="glass-card p-6 flex flex-col" data-testid={`protection-${p.title.split(" ")[0].toLowerCase()}`}>
            <div className={`w-11 h-11 rounded-2xl bg-gradient-to-br ${p.color} text-white grid place-items-center shrink-0`}>
              <p.icon size={20} />
            </div>
            <h2 className="font-bold text-base mt-4">{p.title}</h2>
            <p className="text-sm text-[var(--ink-soft)] mt-2 leading-relaxed">{p.text}</p>
          </article>
        ))}
      </section>

      {/* Conseils */}
      <section className="glass-card p-8 mt-12" data-testid="conseils-section">
        <h2 className="font-bold text-lg inline-flex items-center gap-2">
          <AlertTriangle size={20} className="text-amber-500" /> Nos conseils pour eviter les arnaques
        </h2>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 mt-5">
          {CONSEILS.map((c, i) => (
            <li key={i} className="flex items-start gap-3 text-sm">
              <span className="w-5 h-5 rounded-full bg-orange-100 text-orange-600 grid place-items-center text-[10px] font-bold shrink-0 mt-0.5">{i + 1}</span>
              <span className="text-[var(--ink-soft)]">{c}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Transparence */}
      <section className="glass-card p-8 mt-6 flex flex-col md:flex-row items-start md:items-center gap-6" data-testid="transparence-section">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-sky-500 to-blue-400 text-white grid place-items-center shrink-0">
          <Eye size={22} />
        </div>
        <div className="flex-1">
          <h2 className="font-bold text-lg">Transparence totale</h2>
          <p className="text-sm text-[var(--ink-soft)] mt-1">
            Chaque connexion, paiement et action sensible est journalise et surveille par notre equipe.
            En cas de probleme, ecrivez a <a href="mailto:support@fodindin.net" className="text-orange-600 font-semibold">support@fodindin.net</a> — reponse sous 24h.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="mt-12 text-center rounded-3xl bg-gradient-to-br from-emerald-600 to-teal-500 text-white p-10" data-testid="securite-cta">
        <Fingerprint size={36} className="mx-auto opacity-90" />
        <h2 className="font-display text-2xl font-extrabold mt-3">Protegez votre compte en 30 secondes</h2>
        <p className="text-white/85 text-sm mt-2 max-w-xl mx-auto">
          Activez l&apos;empreinte digitale depuis votre telephone : connexion sans mot de passe et paiements verrouilles, meme en cas de vol.
        </p>
        <Link
          to="/parametres"
          className="inline-flex items-center gap-2 bg-white text-emerald-700 font-bold px-6 py-3 rounded-full mt-5 hover:bg-emerald-50 transition-colors"
          data-testid="cta-activer-biometrie"
        >
          Activer la biometrie <ArrowRight size={16} />
        </Link>
      </section>
    </main>
  );
}
